import { Delaunay } from 'd3-delaunay';
import { poissonDiscSample } from './poisson.js';
import { makeFractalNoise, radialFalloff, type FractalNoise } from './noise.js';
import { makeRng, type Rng } from './rng.js';
import type {
  Biome,
  Cell,
  Continent,
  Edge,
  MapData,
  MapGenOptions,
  Point,
  Region,
} from './types.js';

const BIOME_COLORS: Record<Biome, number> = {
  ocean: 0x1b3a5b,
  coast: 0x2a5179,
  beach: 0xd9c389,
  plains: 0x8fb35a,
  forest: 0x4f8a3f,
  deepforest: 0x2f6a2c,
  desert: 0xd7b56d,
  swamp: 0x5a7250,
  hills: 0xa39257,
  mountain: 0x8a857a,
  snow: 0xe8eef2,
};

export function generateMap(opts: MapGenOptions): MapData {
  const {
    width,
    height,
    seed,
    cellSpacing,
    relaxationIterations,
    cellsPerRegion,
    continentCount,
  } = opts;
  const rng = makeRng(seed);

  // 1. Poisson disc sampling
  const initialPoints = poissonDiscSample(width, height, cellSpacing, rng);

  // 2. Voronoi + Lloyd's relaxation
  const sites = relaxLloyd(initialPoints, width, height, relaxationIterations);
  const flat = new Float64Array(sites.length * 2);
  for (let i = 0; i < sites.length; i++) {
    flat[i * 2] = sites[i]![0];
    flat[i * 2 + 1] = sites[i]![1];
  }
  const delaunay = new Delaunay(flat);
  const voronoi = delaunay.voronoi([0, 0, width, height]);

  // 3. Build cells (raw Voronoi polygons)
  const cells: Cell[] = sites.map((site, i) => {
    const polyRaw = voronoi.cellPolygon(i);
    const polygon: Point[] = polyRaw
      ? (polyRaw.slice(0, polyRaw.length - 1) as unknown as Point[]).map(
          (p) => [p[0], p[1]] as Point,
        )
      : [];
    return {
      index: i,
      site: [site[0], site[1]] as Point,
      polygon,
      displacedPolygon: [],
      edgeIds: [],
      neighbors: Array.from(voronoi.neighbors(i)),
      elevation: 0,
      moisture: 0,
      temperature: 0,
      isOcean: false,
      isCoast: false,
      isLake: false,
      biome: 'ocean',
      continentId: -1,
      regionId: -1,
    };
  });

  // 4. Pick well-spaced continent seeds (Lloyd-relaxed)
  const continentSeeds = pickContinentSeeds(width, height, continentCount, rng);

  // 5. Domain warping — distort each cell's effective position with low-freq noise
  //    BEFORE computing distance to seeds. This breaks the round Voronoi shapes
  //    and turns continents into elongated, irregular forms (peninsulas, isthmuses,
  //    diagonal stretches) like real continents on Earth.
  const warpX = makeFractalNoise(rng, {
    octaves: 3,
    frequency: 1.4 / Math.max(width, height),
  });
  const warpY = makeFractalNoise(rng, {
    octaves: 3,
    frequency: 1.4 / Math.max(width, height),
  });
  const warpAmp = Math.min(width, height) * 0.22;

  // 6. Per-cell: nearest continent seed, distance to it, distance to next-nearest
  //    in WARPED space. Defines the irregular "Voronoi-of-seeds" partition.
  for (const c of cells) {
    const wx = c.site[0] + warpX(c.site[0], c.site[1]) * warpAmp;
    const wy = c.site[1] + warpY(c.site[0], c.site[1]) * warpAmp;
    let nearest = 0;
    let nearestD2 = Infinity;
    let secondD2 = Infinity;
    for (let i = 0; i < continentSeeds.length; i++) {
      const dx = wx - continentSeeds[i]![0];
      const dy = wy - continentSeeds[i]![1];
      const d2 = dx * dx + dy * dy;
      if (d2 < nearestD2) {
        secondD2 = nearestD2;
        nearestD2 = d2;
        nearest = i;
      } else if (d2 < secondD2) {
        secondD2 = d2;
      }
    }
    c.continentId = nearest;
    const dist = Math.sqrt(nearestD2);
    const halfWidth = Math.sqrt(secondD2) / 2;
    (c as Cell & { _distNorm?: number })._distNorm = dist / Math.max(1, halfWidth);
  }

  // 6. Elevation = noise minus distance-from-seed; cells far from their seed go ocean.
  //    This produces 9 organic land masses with noisy coastlines, naturally separated.
  const elevNoise = makeFractalNoise(rng, {
    octaves: 5,
    frequency: 2.4 / Math.max(width, height),
  });
  const moistNoise = makeFractalNoise(rng, {
    octaves: 4,
    frequency: 2.5 / Math.max(width, height),
  });
  const tempNoise = makeFractalNoise(rng, {
    octaves: 3,
    frequency: 1.0 / Math.max(width, height),
  });

  // Direct mask: a cell is land if its distance ratio (distNorm ∈ [0, 2]) is
  // below a noise-perturbed threshold. With an average distNorm ≈ 1.2 across
  // the world, threshold = 1.65 gives ~65% land. The edge falloff makes sure
  // the world borders always end in ocean.
  const LAND_THRESHOLD = 1.65;
  const NOISE_AMPLITUDE = 0.45;
  for (const c of cells) {
    const [x, y] = c.site;
    const distNorm = (c as Cell & { _distNorm?: number })._distNorm ?? 0;
    const baseElev = elevNoise(x, y); // [-1,1]
    const edgeFalloff = radialFalloff(x, y, width, height, 1);
    const noisyThresh = LAND_THRESHOLD + baseElev * NOISE_AMPLITUDE - edgeFalloff * 0.85;
    // Elevation = how deep inside the land we are. >0 land, <0 ocean.
    c.elevation = Math.max(-1, Math.min(1, (noisyThresh - distNorm) * 0.8));
    c.moisture = (moistNoise(x, y) + 1) / 2;
    const latNorm = Math.abs((y / height) * 2 - 1);
    c.temperature = (tempNoise(x, y) + 1) / 2 - latNorm * 0.5;
  }

  // 7. Sea level → ocean cells, then flood-fill from edges to identify lakes vs ocean.
  for (const c of cells) {
    if (c.elevation < opts.seaLevel) c.isOcean = true;
  }
  markRealOcean(cells, width, height);

  // 7b. Drop isolated land fragments. After noise carves out ocean, a continent
  //     can end up split into a main mass and a few tiny "ghost islands" of
  //     1-3 cells floating in the strait. They render as buggy mini-territories
  //     and break the visual. We keep only the LARGEST connected land component
  //     per continent; the rest is sunk back to ocean.
  removeIsolatedFragments(cells, continentSeeds.length);

  // 7c. Build land bridges between continents — every continent ends up reachable
  //     from every other continent via at least one isthmus. Uses an MST over
  //     continent centroids, then flips ocean cells to land along each MST edge.
  buildContinentBridges(cells, continentSeeds);

  // 8. Coast detection
  for (const c of cells) {
    if (c.isOcean) continue;
    for (const ni of c.neighbors) {
      if (cells[ni]!.isOcean) {
        c.isCoast = true;
        break;
      }
    }
  }

  // 9. Biomes
  for (const c of cells) {
    c.biome = pickBiome(c);
  }

  // 10. Build the Continent[] from continentId. Each seed produces one continent;
  //     non-land cells just won't appear in any continent's `cells` list.
  //     Discard any continent that ended up with too few land cells (very rare with
  //     well-spaced seeds, but defensive).
  const continents = buildContinentsFromSeeds(cells, continentSeeds);

  // 11. Regions = clusters of land cells inside each continent, capped globally.
  const regions = buildRegions(cells, continents, cellsPerRegion, opts.maxRegions, rng);
  for (const region of regions) {
    // Per-region tint variation (~±9% lightness) so two adjacent regions of the
    // same biome don't merge visually into one blob.
    region.color = jitterColor(BIOME_COLORS[region.biome], (rng() - 0.5) * 0.18);
  }

  // 12. Build the unique edge graph: every Voronoi edge is stored ONCE, both
  //     adjacent cells reference it by id. This is the foundation for clean borders.
  const edges = buildEdgeGraph(cells);

  // 13. Displace each edge once. Endpoints are tapered to zero so the displacement
  //     is 0 at vertices — every edge meeting at a Voronoi corner stays connected.
  const displaceNoise = makeFractalNoise(rng, {
    octaves: 3,
    frequency: 8 / Math.max(width, height),
  });
  const amount = opts.edgeDisplacement * cellSpacing;
  for (const e of edges) {
    e.polyline = displaceEdge(e, displaceNoise, amount);
  }

  // 14. Reconstruct each cell's polygon from displaced edge polylines. Shared edges
  //     mean adjacent cells are perfectly contiguous (no gap, no overlap).
  for (const c of cells) {
    c.displacedPolygon = buildCellDisplacedPolygon(c, edges);
  }

  return {
    width,
    height,
    seed,
    cells,
    edges,
    regions,
    continents,
    seaLevel: opts.seaLevel,
  };
}

// ============================================================================
// Continent seeds
// ============================================================================

/**
 * Pick `count` well-distributed seed points using random init + Lloyd's relaxation
 * on a Voronoi of those seeds (so they spread evenly across the world, kept away
 * from the borders by a margin).
 */
function pickContinentSeeds(width: number, height: number, count: number, rng: Rng): Point[] {
  const margin = Math.min(width, height) * 0.13;
  let pts: Array<[number, number]> = [];
  for (let i = 0; i < count; i++) {
    pts.push([margin + rng() * (width - 2 * margin), margin + rng() * (height - 2 * margin)]);
  }
  for (let iter = 0; iter < 12; iter++) {
    const flat = new Float64Array(pts.length * 2);
    for (let i = 0; i < pts.length; i++) {
      flat[i * 2] = pts[i]![0];
      flat[i * 2 + 1] = pts[i]![1];
    }
    const d = new Delaunay(flat);
    const v = d.voronoi([margin, margin, width - margin, height - margin]);
    pts = pts.map((p, i) => {
      const poly = v.cellPolygon(i);
      if (!poly) return p;
      return centroid(poly as unknown as Array<[number, number]>);
    });
  }
  return pts.map((p) => [p[0], p[1]] as Point);
}

function buildContinentsFromSeeds(cells: Cell[], seeds: Point[]): Continent[] {
  const continents: Continent[] = seeds.map((seed, id) => ({
    id,
    cells: [],
    regions: [],
    seed,
    bounds: { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
  }));
  for (const c of cells) {
    if (c.isOcean) continue;
    const cont = continents[c.continentId];
    if (!cont) continue;
    cont.cells.push(c.index);
    const [x, y] = c.site;
    if (x < cont.bounds.minX) cont.bounds.minX = x;
    if (y < cont.bounds.minY) cont.bounds.minY = y;
    if (x > cont.bounds.maxX) cont.bounds.maxX = x;
    if (y > cont.bounds.maxY) cont.bounds.maxY = y;
  }
  return continents;
}

// ============================================================================
// Lloyd / centroid helpers
// ============================================================================

function relaxLloyd(
  points: Array<[number, number]>,
  width: number,
  height: number,
  iterations: number,
): Array<[number, number]> {
  let pts = points.map((p) => [p[0], p[1]] as [number, number]);
  for (let iter = 0; iter < iterations; iter++) {
    const flat = new Float64Array(pts.length * 2);
    for (let i = 0; i < pts.length; i++) {
      flat[i * 2] = pts[i]![0];
      flat[i * 2 + 1] = pts[i]![1];
    }
    const d = new Delaunay(flat);
    const v = d.voronoi([0, 0, width, height]);
    pts = pts.map((p, i) => {
      const poly = v.cellPolygon(i);
      if (!poly) return p;
      return centroid(poly as unknown as Array<[number, number]>);
    });
  }
  return pts;
}

function centroid(polygon: Array<[number, number]>): [number, number] {
  let sx = 0;
  let sy = 0;
  let n = 0;
  const len = polygon.length - 1;
  for (let i = 0; i < len; i++) {
    sx += polygon[i]![0];
    sy += polygon[i]![1];
    n++;
  }
  return n > 0 ? [sx / n, sy / n] : polygon[0]!;
}

// ============================================================================
// Ocean / lake / biome
// ============================================================================

function markRealOcean(_cells: Cell[], _width: number, _height: number): void {
  // Intentionally a no-op. Earlier versions converted inland water cells (those
  // not connected to the world boundary via ocean) into "lakes" (isOcean=false,
  // isLake=true, biome='ocean'). The problem: lake cells then participated in
  // region clustering and biome counting. A region with a majority of lake
  // cells got region.biome='ocean' and a blue color, which was then applied to
  // ALL its cells (snow / hills / etc.) — producing a single "territory" with
  // two contradictory colors at two different platform heights.
  //
  // Treating every below-sea-level cell as ocean is simpler and visually
  // correct: inland water just shows the ocean plane through the gap, with
  // surrounding land cells generating their normal coastlines and cliff walls.
}

/**
 * Sink any tiny disconnected land fragment of a continent back to ocean.
 * For each continent we BFS through `cell.neighbors` (restricted to non-ocean
 * cells of the same continent) to enumerate connected components. Only the
 * LARGEST component is kept; smaller fragments have isOcean=true and lose
 * their continent assignment. This prevents the "buggy mini-island" artefacts
 * (1-3 cell territories floating in the strait between two continents).
 */
function removeIsolatedFragments(cells: Cell[], continentCount: number): void {
  for (let cId = 0; cId < continentCount; cId++) {
    const visited = new Set<number>();
    const components: number[][] = [];
    for (const c of cells) {
      if (c.isOcean || c.continentId !== cId) continue;
      if (visited.has(c.index)) continue;
      const comp: number[] = [];
      const stack = [c.index];
      visited.add(c.index);
      while (stack.length > 0) {
        const ci = stack.pop()!;
        comp.push(ci);
        for (const ni of cells[ci]!.neighbors) {
          if (visited.has(ni)) continue;
          const n = cells[ni]!;
          if (n.isOcean || n.continentId !== cId) continue;
          visited.add(ni);
          stack.push(ni);
        }
      }
      components.push(comp);
    }
    if (components.length <= 1) continue;
    components.sort((a, b) => b.length - a.length);
    // Keep components[0]; sink all the smaller ones.
    for (let i = 1; i < components.length; i++) {
      for (const ci of components[i]!) {
        cells[ci]!.isOcean = true;
        cells[ci]!.continentId = -1;
      }
    }
  }
}

/**
 * Ensure every continent is reachable from every other via a chain of land cells.
 *
 * Strategy:
 *   1. Compute, for each pair of continents, the closest pair of LAND cells.
 *   2. Run Kruskal's algorithm to extract a Minimum Spanning Tree over the
 *      continent graph weighted by these distances.
 *   3. For each MST edge, greedy-walk from one closest cell to the other along
 *      Voronoi neighbors, flipping ocean cells to land. Each flipped bridge
 *      cell is reassigned to the closer of the two endpoint continents so the
 *      isthmus belongs cleanly to one or the other (no third-party leakage).
 *
 * Result: 8 isthmuses for 9 continents, the global continent graph is a tree.
 */
function buildContinentBridges(cells: Cell[], continentSeeds: Point[]): void {
  // Group land cells per continent
  const continentCells: number[][] = continentSeeds.map(() => []);
  for (const c of cells) {
    if (c.isOcean) continue;
    if (c.continentId >= 0 && c.continentId < continentCells.length) {
      continentCells[c.continentId]!.push(c.index);
    }
  }
  const valid = continentSeeds
    .map((_, i) => i)
    .filter((i) => continentCells[i]!.length > 0);
  if (valid.length < 2) return;

  type Pair = { a: number; b: number; d: number; ca: number; cb: number };
  const pairs: Pair[] = [];

  for (let i = 0; i < valid.length; i++) {
    for (let j = i + 1; j < valid.length; j++) {
      const a = valid[i]!;
      const b = valid[j]!;
      let bestD = Infinity;
      let bestCa = -1;
      let bestCb = -1;
      for (const ca of continentCells[a]!) {
        const sa = cells[ca]!.site;
        for (const cb of continentCells[b]!) {
          const sb = cells[cb]!.site;
          const dx = sa[0] - sb[0];
          const dy = sa[1] - sb[1];
          const d = dx * dx + dy * dy;
          if (d < bestD) {
            bestD = d;
            bestCa = ca;
            bestCb = cb;
          }
        }
      }
      if (bestCa >= 0 && bestCb >= 0) {
        pairs.push({ a, b, d: Math.sqrt(bestD), ca: bestCa, cb: bestCb });
      }
    }
  }

  // Kruskal's MST
  pairs.sort((p, q) => p.d - q.d);
  const parent = new Map<number, number>();
  for (const v of valid) parent.set(v, v);
  const find = (x: number): number => {
    let r = x;
    while (parent.get(r)! !== r) r = parent.get(r)!;
    // path compression
    let cur = x;
    while (parent.get(cur)! !== r) {
      const next = parent.get(cur)!;
      parent.set(cur, r);
      cur = next;
    }
    return r;
  };
  const union = (x: number, y: number): boolean => {
    const rx = find(x);
    const ry = find(y);
    if (rx === ry) return false;
    parent.set(rx, ry);
    return true;
  };

  const mst: Pair[] = [];
  for (const p of pairs) {
    if (union(p.a, p.b)) {
      mst.push(p);
      if (mst.length === valid.length - 1) break;
    }
  }

  // Carve a bridge for each MST edge
  for (const e of mst) {
    carveBridge(cells, continentSeeds, e.ca, e.cb, e.a, e.b);
  }
}

function carveBridge(
  cells: Cell[],
  continentSeeds: Point[],
  startIdx: number,
  endIdx: number,
  contA: number,
  contB: number,
): void {
  const targetSite = cells[endIdx]!.site;
  const seedA = continentSeeds[contA]!;
  const seedB = continentSeeds[contB]!;

  let current = startIdx;
  const visited = new Set<number>();
  visited.add(current);

  // Greedy walk: at each step, move to the unvisited neighbor closest to the
  // target. Cap iterations defensively in case of pathological geometry.
  const MAX_STEPS = 250;
  for (let step = 0; step < MAX_STEPS; step++) {
    if (current === endIdx) break;
    const c = cells[current]!;

    // Already adjacent to target? Done — we don't need to flip the target itself.
    if (c.neighbors.includes(endIdx)) break;

    let bestNi = -1;
    let bestD = Infinity;
    for (const ni of c.neighbors) {
      if (visited.has(ni)) continue;
      const n = cells[ni]!;
      const dx = n.site[0] - targetSite[0];
      const dy = n.site[1] - targetSite[1];
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        bestNi = ni;
      }
    }
    if (bestNi === -1) break;

    visited.add(bestNi);
    const n = cells[bestNi]!;
    if (n.isOcean) {
      n.isOcean = false;
      n.isLake = false;
      // Reassign continentId to whichever endpoint continent is closer in raw
      // (un-warped) space. Prevents the bridge from "leaking" into a third
      // continent if the path crosses its Voronoi region.
      const dxA = n.site[0] - seedA[0];
      const dyA = n.site[1] - seedA[1];
      const dxB = n.site[0] - seedB[0];
      const dyB = n.site[1] - seedB[1];
      n.continentId = dxA * dxA + dyA * dyA < dxB * dxB + dyB * dyB ? contA : contB;
      // Boost elevation just above sea level so biome picker treats it as plains/coast
      n.elevation = Math.max(n.elevation, 0.05);
    }
    current = bestNi;
  }
}

function pickBiome(c: Cell): Biome {
  if (c.isOcean) return 'ocean';
  if (c.isLake) return 'ocean';
  if (c.isCoast && c.elevation < 0.05) return 'beach';

  if (c.elevation > 0.55) return 'snow';
  if (c.elevation > 0.4) return 'mountain';
  if (c.elevation > 0.25) return 'hills';

  const m = c.moisture;
  const t = c.temperature;

  if (t < 0.15) return c.elevation > 0.15 ? 'mountain' : 'snow';
  if (m < 0.25 && t > 0.45) return 'desert';
  if (m > 0.7 && t > 0.4) return 'swamp';
  if (m > 0.55) return 'deepforest';
  if (m > 0.4) return 'forest';
  return 'plains';
}

// ============================================================================
// Region clustering with global cap
// ============================================================================

function allocateRegionsAcrossContinents(
  continents: Continent[],
  cellsPerRegion: number,
  maxRegions: number,
): number[] {
  if (continents.length === 0) return [];
  // ignore continents that ended up empty (no land)
  const nonEmpty = continents.filter((c) => c.cells.length > 0);
  if (nonEmpty.length === 0) return continents.map(() => 0);

  const totalLand = nonEmpty.reduce((s, c) => s + c.cells.length, 0);
  const naturalTarget = Math.max(nonEmpty.length, Math.ceil(totalLand / cellsPerRegion));
  const target = Math.min(maxRegions, naturalTarget);

  const out = new Array<number>(continents.length).fill(0);

  if (target < nonEmpty.length) {
    // Not enough budget for one region per non-empty continent: largest win.
    const order = nonEmpty
      .map((c) => ({ size: c.cells.length, id: c.id }))
      .sort((a, b) => b.size - a.size);
    for (let k = 0; k < target; k++) out[order[k]!.id] = 1;
    return out;
  }

  const shares = continents.map((c) => (c.cells.length / totalLand) * target);
  for (let i = 0; i < continents.length; i++) {
    out[i] = continents[i]!.cells.length > 0 ? Math.max(1, Math.floor(shares[i]!)) : 0;
  }
  let sum = out.reduce((a, b) => a + b, 0);
  while (sum > target) {
    let maxIdx = 0;
    for (let i = 1; i < out.length; i++) {
      if (out[i]! > out[maxIdx]!) maxIdx = i;
    }
    if (out[maxIdx]! <= 1) break;
    out[maxIdx]!--;
    sum--;
  }
  const remainders = shares
    .map((s, i) => ({ frac: s - Math.floor(s), i }))
    .filter((r) => continents[r.i]!.cells.length > 0)
    .sort((a, b) => b.frac - a.frac);
  let ri = 0;
  while (sum < target && remainders.length > 0) {
    out[remainders[ri % remainders.length]!.i]!++;
    sum++;
    ri++;
  }
  return out;
}

function buildRegions(
  cells: Cell[],
  continents: Continent[],
  cellsPerRegion: number,
  maxRegions: number,
  rng: Rng,
): Region[] {
  const regions: Region[] = [];
  const allocations = allocateRegionsAcrossContinents(continents, cellsPerRegion, maxRegions);

  // Re-use a single Int32Array across continents (faster than reallocating).
  const cellRegion = new Int32Array(cells.length).fill(-1);

  for (let cIdx = 0; cIdx < continents.length; cIdx++) {
    const continent = continents[cIdx]!;
    const target = allocations[cIdx]!;
    if (continent.cells.length === 0 || target === 0) continue;

    const available = continent.cells.slice();
    shuffle(available, rng);
    const seeds = available.slice(0, target);

    // CRITICAL: capture regions.length BEFORE pushing any of this continent's
    // regions. Using `regions.length + k` inside the forEach is buggy because
    // regions.length grows with each push — IDs would become oldLen, oldLen+2,
    // oldLen+4 (off-by-k), no longer matching their array index.
    const baseId = regions.length;
    const queues: number[][] = [];

    seeds.forEach((s, k) => {
      const id = baseId + k;
      cellRegion[s] = id;
      cells[s]!.regionId = id;
      regions.push({
        id,
        cells: [s],
        continentId: continent.id,
        biome: 'plains',
        centroid: cells[s]!.site,
        color: 0,
        area: 0,
      });
      continent.regions.push(id);
      queues.push([s]);
    });

    let progress = true;
    while (progress) {
      progress = false;
      for (let qi = 0; qi < queues.length; qi++) {
        const queue = queues[qi]!;
        const next: number[] = [];
        const regionId = baseId + qi;
        for (const ci of queue) {
          for (const ni of cells[ci]!.neighbors) {
            if (cellRegion[ni] !== -1) continue;
            const n = cells[ni]!;
            if (n.isOcean || n.continentId !== continent.id) continue;
            cellRegion[ni] = regionId;
            regions[regionId]!.cells.push(ni);
            cells[ni]!.regionId = regionId;
            next.push(ni);
            progress = true;
          }
        }
        queues[qi] = next;
      }
    }
  }

  // centroid + dominant biome + area
  for (const region of regions) {
    if (region.cells.length === 0) continue;
    let sx = 0;
    let sy = 0;
    let area = 0;
    const biomeCount = new Map<Biome, number>();
    for (const ci of region.cells) {
      const c = cells[ci]!;
      sx += c.site[0];
      sy += c.site[1];
      biomeCount.set(c.biome, (biomeCount.get(c.biome) ?? 0) + 1);
      area += polygonArea(c.polygon);
    }
    region.centroid = [sx / region.cells.length, sy / region.cells.length];
    region.area = area;
    let best: Biome = 'plains';
    let bestN = -1;
    for (const [b, n] of biomeCount) {
      if (n > bestN) {
        bestN = n;
        best = b;
      }
    }
    region.biome = best;
  }

  return regions;
}

function jitterColor(rgb: number, amount: number): number {
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const factor = 1 + amount;
  const cl = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return (cl(r * factor) << 16) | (cl(g * factor) << 8) | cl(b * factor);
}

function polygonArea(poly: Point[]): number {
  if (poly.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    a += p[0] * q[1] - q[0] * p[1];
  }
  return Math.abs(a) / 2;
}

function shuffle<T>(arr: T[], rng: Rng): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = (rng() * (i + 1)) | 0;
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
}

// ============================================================================
// Edge graph — the foundation for non-doubled borders
// ============================================================================

const VERT_PRECISION = 100; // vertices snapped to 0.01 px for canonical keys

function vertKey(p: Point): string {
  return `${Math.round(p[0] * VERT_PRECISION)}_${Math.round(p[1] * VERT_PRECISION)}`;
}

function edgeKey(a: Point, b: Point): string {
  // unordered key: smaller-key vertex first
  const ka = vertKey(a);
  const kb = vertKey(b);
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

function pointEqApprox(a: Point, b: Point): boolean {
  return vertKey(a) === vertKey(b);
}

function buildEdgeGraph(cells: Cell[]): Edge[] {
  const edges: Edge[] = [];
  const seen = new Map<string, number>();

  for (const c of cells) {
    const poly = c.polygon;
    if (poly.length < 2) continue;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i]!;
      const b = poly[(i + 1) % poly.length]!;
      const key = edgeKey(a, b);

      let id = seen.get(key);
      if (id === undefined) {
        // determine partner cell (the other side of this edge)
        const partner = neighborSharingEdge(c, cells, a, b);
        id = edges.length;
        edges.push({
          id,
          v0: a,
          v1: b,
          cellA: c.index,
          cellB: partner,
          polyline: [a, b],
        });
        seen.set(key, id);
      }
      c.edgeIds.push(id);
    }
  }
  return edges;
}

function neighborSharingEdge(c: Cell, cells: Cell[], a: Point, b: Point): number {
  const ka = vertKey(a);
  const kb = vertKey(b);
  for (const ni of c.neighbors) {
    const np = cells[ni]!.polygon;
    let foundA = false;
    let foundB = false;
    for (const p of np) {
      const k = vertKey(p);
      if (k === ka) foundA = true;
      else if (k === kb) foundB = true;
      if (foundA && foundB) return ni;
    }
  }
  return -1;
}

// ============================================================================
// Edge displacement (with endpoint tapering)
// ============================================================================

function displaceEdge(edge: Edge, noise: FractalNoise, amount: number): Point[] {
  const a = edge.v0;
  const b = edge.v1;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < 1 || amount <= 0) return [a, b];

  const nx = -dy / len;
  const ny = dx / len;
  const steps = 6; // 6 segments → 5 inner points (denser sampling = better smoothing)
  const raw: Point[] = [a];
  for (let s = 1; s < steps; s++) {
    const t = s / steps;
    const px = a[0] + dx * t;
    const py = a[1] + dy * t;
    // Sin taper: 0 at endpoints (so vertices stay shared between adjacent edges),
    // peak at middle. Keeps the edge graph contiguous.
    const taper = Math.sin(t * Math.PI);
    const off = noise(px, py) * amount * taper;
    raw.push([px + nx * off, py + ny * off] as Point);
  }
  raw.push(b);
  // Chaikin smoothing turns the noise-displaced polyline into a confident
  // hand-drawn curve. Endpoints are preserved (essential for shared vertices).
  return chaikinSmooth(raw, 2);
}

/**
 * Chaikin's corner-cutting algorithm. Each iteration replaces every internal
 * segment with two new points at 1/4 and 3/4 of its length, which converges
 * toward a smooth quadratic B-spline. Endpoints are kept fixed so the curve
 * stays anchored at Voronoi vertices.
 */
function chaikinSmooth(pts: Point[], iterations: number): Point[] {
  let result = pts;
  for (let iter = 0; iter < iterations; iter++) {
    if (result.length < 3) return result;
    const next: Point[] = [result[0]!];
    for (let i = 0; i < result.length - 1; i++) {
      const p1 = result[i]!;
      const p2 = result[i + 1]!;
      next.push([0.75 * p1[0] + 0.25 * p2[0], 0.75 * p1[1] + 0.25 * p2[1]] as Point);
      next.push([0.25 * p1[0] + 0.75 * p2[0], 0.25 * p1[1] + 0.75 * p2[1]] as Point);
    }
    next.push(result[result.length - 1]!);
    result = next;
  }
  return result;
}

// ============================================================================
// Cell polygon reconstructed from displaced edges
// ============================================================================

function buildCellDisplacedPolygon(c: Cell, edges: Edge[]): Point[] {
  const out: Point[] = [];
  const poly = c.polygon;
  if (poly.length < 2 || c.edgeIds.length !== poly.length) return poly;

  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    const e = edges[c.edgeIds[i]!]!;
    // walk the polyline in the right direction (a → b)
    const sameDir = pointEqApprox(e.v0, a) && pointEqApprox(e.v1, b);
    const pl = sameDir ? e.polyline : [...e.polyline].reverse();
    // append all but the last point — the next iteration's first point is the same vertex
    for (let k = 0; k < pl.length - 1; k++) {
      out.push(pl[k]!);
    }
  }
  return out;
}
